/**
 * S8 \u8f93\u5165\u4e0e\u4ea4\u4e92 \u2014\u2014 \u5b9e\u73b0\u81ea design/gdd/systems/S8-input-interaction.md\u3002
 *
 * \u4e24\u79cd\u624b\u52bf\u5e76\u5b58\uff0c\u5747\u4e3a\u4e00\u7b49\u8def\u5f84\uff1a
 *   \u62d6\u62fd\u8def\u5f84\uff1a\u6309\u4e0b\u5e76\u79fb\u52a8\u8d85\u8fc7\u542f\u52a8\u9608\u503c \u2192 \u8fdb\u5165\u62d6\u62fd\u6001 \u2192 \u53f0\u624b\u65f6\u6309\u3010\u6307\u9488\u6240\u5728\u683c\u3011\u5224\u5b9a\u76ee\u6807
 *   \u70b9\u51fb\u8def\u5f84\uff1a\u6309\u4e0b\u642c\u8d77\u672a\u8d85\u9608\u503c \u2192 \u9009\u4e2d\uff1b\u518d\u70b9\u76f8\u90bb\u683c \u2192 \u4ea4\u6362
 *
 * \u5173\u952e\uff1a\u62d6\u62fd\u671f\u95f4\u5fc5\u987b\u6709\u5b9e\u65f6\u53ef\u89c6\u53cd\u9988\uff08\u68cb\u5b50\u8ddf\u624b\uff09\uff0c\u5426\u5219\u73a9\u5bb6\u4f1a\u8bef\u4ee5\u4e3a\u6ca1\u53cd\u5e94\u3002
 */

import type { Coord } from '../core/types';
import { BOARD_SIZE } from '../core/types';

/** \u5f00\u59cb\u8fdb\u5165\u62d6\u62fd\u6001\u7684\u6700\u5c0f\u4f4d\u79fb\uff08\u76f8\u5bf9\u5355\u683c\uff09\uff1a\u5c0f\u4e00\u70b9\u66f4\u7075\u654f */
export const DRAG_START_RATIO = 0.22;
/** \u4fdd\u7559\u539f\u540d\u4f9b\u5176\u4ed6\u6a21\u5757\u5f15\u7528 */
export const SWEEP_THRESHOLD_RATIO = DRAG_START_RATIO;

export interface BoardLayout {
  /** \u68cb\u76d8\u5de6\u4e0a\u89d2\uff08CSS \u50cf\u7d20\uff09 */
  ox: number;
  oy: number;
  /** \u5355\u683c\u5c3a\u5bf8\uff08\u542b\u95f4\u8ddd\uff09 */
  cellPitch: number;
}

export type SelectionState = 'NONE' | 'SELECTED';

export interface DragVisual {
  /** \u88ab\u62d6\u62fd\u7684\u8d77\u70b9\u683c */
  from: Coord | null;
  /** \u5f53\u524d\u624b\u6307/\u6307\u9488\u843d\u5728\u54ea\u4e00\u683c\uff08\u53ef\u80fd\u8d8a\u754c\uff09 */
  over: Coord | null;
  /** \u76f8\u5bf9\u8d77\u70b9\u7684\u50cf\u7d20\u4f4d\u79fb */
  dx: number;
  dy: number;
  /** \u662f\u5426\u5df2\u8d85\u8fc7\u542f\u52a8\u9608\u503c\uff08\u51b3\u5b9a\u662f\u5426\u8fdb\u5165\u62d6\u62fd\u6001\uff09 */
  active: boolean;
}

export interface InputCallbacks {
  onSwapIntent(from: Coord, to: Coord): void;
  onSelectionChanged(state: SelectionState, coord?: Coord): void;
  /** \u62d6\u62fd\u4e2d\u7684\u5b9e\u65f6\u72b6\u6001\uff08\u6bcf\u5e27\u53ef\u80fd\u591a\u6b21\uff09 */
  onDragVisual(v: DragVisual): void;
  /** \u60ac\u505c\u683c\u53d8\u5316\uff08\u4ec5\u9f20\u6807\u3001\u975e\u6309\u4e0b\u72b6\u6001\uff1b\u89e6\u5c4f\u8bbe\u5907\u4e0d\u4ea7\u751f hover\uff09 */
  onHover?(coord: Coord | null): void;
}

export class InputController {
  private el: HTMLElement;
  private layout: BoardLayout;
  private cb: InputCallbacks;

  private pointerActive = false;
  private pointerId: number | null = null;
  private startCoord: Coord | null = null;
  private startX = 0;
  private startY = 0;
  private selected: Coord | null = null;
  /** \u662f\u5426\u5df2\u8fdb\u5165\u62d6\u62fd\u6001\uff08\u8d85\u8fc7\u542f\u52a8\u9608\u503c\uff09 */
  private dragging = false;
  /** \u4e0a\u6b21\u60ac\u505c\u683c\uff08\u907f\u514d\u91cd\u590d\u56de\u8c03\uff09 */
  private lastHover: Coord | null = null;

  private disposers: (() => void)[] = [];

  constructor(el: HTMLElement, layout: BoardLayout, cb: InputCallbacks) {
    this.el = el;
    this.layout = layout;
    this.cb = cb;
    // \u68cb\u76d8\u533a\u57df\u7981\u6b62\u6d4f\u89c8\u5668\u9ed8\u8ba4\u624b\u52bf\uff08\u9632\u6eda\u52a8/\u7f29\u653e\u62a2\u4e8b\u4ef6\uff09
    this.el.style.touchAction = 'none';
    this.bind();
  }

  setLayout(layout: BoardLayout): void { this.layout = layout; }

  private bind(): void {
    const down = (e: PointerEvent) => this.onDown(e);
    const move = (e: PointerEvent) => this.onMove(e);
    const up = (e: PointerEvent) => this.onUp(e);
    const cancel = (e: PointerEvent) => this.onCancel(e);

    this.el.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);

    this.disposers.push(
      () => this.el.removeEventListener('pointerdown', down),
      () => window.removeEventListener('pointermove', move),
      () => window.removeEventListener('pointerup', up),
      () => window.removeEventListener('pointercancel', cancel),
    );
  }

  dispose(): void {
    for (const d of this.disposers) d();
    this.disposers = [];
  }

  clearSelection(): void {
    this.selected = null;
    this.cb.onSelectionChanged('NONE');
  }

  getSelection(): Coord | null { return this.selected; }

  /** \u5c4f\u5e55\u5750\u6807 \u2192 \u68cb\u76d8\u5750\u6807\uff08\u8d8a\u754c\u8fd4\u56de null\uff09 */
  private hitTest(clientX: number, clientY: number): Coord | null {
    const rect = this.el.getBoundingClientRect();
    const x = clientX - rect.left - this.layout.ox;
    const y = clientY - rect.top - this.layout.oy;
    const c = Math.floor(x / this.layout.cellPitch);
    const r = Math.floor(y / this.layout.cellPitch);
    if (c < 0 || c >= BOARD_SIZE || r < 0 || r >= BOARD_SIZE) return null;
    return { c, r };
  }

  /** \u5c4f\u5e55\u5750\u6807 \u2192 \u6700\u8fd1\u7684\u683c\uff08\u5373\u4f7f\u8d8a\u754c\u4e5f\u88c1\u526a\u5230\u8fb9\u754c\uff09 */
  private nearestCell(clientX: number, clientY: number): Coord {
    const rect = this.el.getBoundingClientRect();
    const x = clientX - rect.left - this.layout.ox;
    const y = clientY - rect.top - this.layout.oy;
    const c = Math.round(x / this.layout.cellPitch - 0.5);
    const r = Math.round(y / this.layout.cellPitch - 0.5);
    return {
      c: Math.max(0, Math.min(BOARD_SIZE - 1, c)),
      r: Math.max(0, Math.min(BOARD_SIZE - 1, r)),
    };
  }

  private onDown(e: PointerEvent): void {
    // E6\uff1a\u591a\u70b9\u89e6\u63a7\u53ea\u8ba4\u9996\u4e2a\u89e6\u70b9
    if (this.pointerActive) return;
    const coord = this.hitTest(e.clientX, e.clientY);
    if (!coord) return;

    this.pointerActive = true;
    this.pointerId = e.pointerId;
    this.startCoord = coord;
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.dragging = false;

    // \u6309\u4e0b\u65f6\u5148\u4e0d\u505a\u4efb\u4f55\u4ea4\u6362\uff0c\u7b49 pointermove \u5224\u5b9a\u662f\u62d6\u62fd\u8fd8\u662f\u70b9\u51fb\u3002
    // \u82e5\u5df2\u6709\u9009\u4e2d\u683c\u4e14\u6309\u5728\u76f8\u90bb\u683c\u4e0a\uff0c\u4f5c\u4e3a\u5019\u9009\u76ee\u6807\u8bb0\u4e0b\uff0c\u5728 pointerup \u65f6\u82e5\u672a\u62d6\u52a8\u5219\u6267\u884c\uff08\u8f85\u8def\u5f84\u7b2c\u4e8c\u6b65\uff09\u3002
    this.cb.onDragVisual({ from: coord, over: coord, dx: 0, dy: 0, active: false });
  }

  private onMove(e: PointerEvent): void {
    // 悬停反馈（仅鼠标、非按住状态）：扫过棋盘时高亮所在格
    if (!this.pointerActive && e.pointerType === 'mouse' && this.cb.onHover) {
      const coord = this.hitTest(e.clientX, e.clientY);
      const prev = this.lastHover;
      const changed = (coord === null) !== (prev === null)
        || (coord && prev && (coord.c !== prev.c || coord.r !== prev.r));
      if (changed) {
        this.lastHover = coord;
        this.cb.onHover(coord);
      }
      if (!this.pointerActive) return;
    }
    if (!this.pointerActive || this.pointerId !== e.pointerId) return;
    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;
    const threshold = this.layout.cellPitch * DRAG_START_RATIO;

    if (!this.dragging && Math.max(Math.abs(dx), Math.abs(dy)) >= threshold) {
      this.dragging = true;
      // \u8fdb\u5165\u62d6\u62fd\u6001\u65f6\u53d6\u6d88\u70b9\u51fb\u9009\u4e2d\uff08\u4e24\u8005\u4e92\u65a5\uff09
      if (this.selected) this.clearSelection();
    }

    if (this.dragging && this.startCoord) {
      this.cb.onDragVisual({
        from: this.startCoord,
        over: this.nearestCell(e.clientX, e.clientY),
        dx, dy,
        active: true,
      });
    }
  }

  private onUp(e: PointerEvent): void {
    if (!this.pointerActive || this.pointerId !== e.pointerId) return;
    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;
    const threshold = this.layout.cellPitch * DRAG_START_RATIO;

    const coordinated = this.startCoord;
    const wasDragging = this.dragging;

    this.pointerActive = false;
    this.pointerId = null;
    this.dragging = false;
    this.startCoord = null;

    // \u6e05\u7a7a\u62d6\u62fd\u53ef\u89c6\u6001
    this.cb.onDragVisual({ from: null, over: null, dx: 0, dy: 0, active: false });

    if (!coordinated) return;

    // \u3010\u62d6\u62fd\u8def\u5f84\u3011\u8d85\u8fc7\u542f\u52a8\u9608\u503c \u2192 \u6309\u79bb\u624b\u65f6\u6307\u9488\u6240\u5728\u683c\u5224\u5b9a\u76ee\u6807
    if (wasDragging) {
      const target = this.nearestCell(e.clientX, e.clientY);
      if (target.c === coordinated.c && target.r === coordinated.r) return; // \u539f\u5730\u653e\u4e0b
      const d = Math.abs(target.c - coordinated.c) + Math.abs(target.r - coordinated.r);
      // \u76f8\u90bb\u683c\u76f4\u63a5\u6362\uff1b\u8df3\u8fc7\u4e2d\u95f4\u683c\u65f6\u53d6\u9996\u4e2a\u65b9\u5411\u6b65
      if (d === 1) {
        this.cb.onSwapIntent(coordinated, target);
      } else {
        const step = this.firstStepToward(coordinated, target);
        if (step) this.cb.onSwapIntent(coordinated, step);
      }
      return;
    }

    // \u3010\u70b9\u51fb\u8def\u5f84\u3011\u672a\u8d85\u9608\u503c \u2192 \u9009\u4e2d / \u4e8c\u6b21\u70b9\u51fb\u4ea4\u6362 / \u53d6\u6d88
    const moved = Math.max(Math.abs(dx), Math.abs(dy));
    if (moved >= threshold) return;

    if (this.selected) {
      const d = Math.abs(this.selected.c - coordinated.c) + Math.abs(this.selected.r - coordinated.r);
      if (d === 1) {
        this.cb.onSwapIntent(this.selected, coordinated);
        this.clearSelection();
        return;
      }
      if (d === 0) {
        this.clearSelection();
        return;
      }
      // \u70b9\u5728\u975e\u76f8\u90bb\u7a7a\u683c \u2192 \u6539\u9009\u4e2d\u65b0\u683c
      this.selected = coordinated;
      this.cb.onSelectionChanged('SELECTED', coordinated);
      return;
    }

    this.selected = coordinated;
    this.cb.onSelectionChanged('SELECTED', coordinated);
  }

  private onCancel(e: PointerEvent): void {
    if (this.pointerId !== e.pointerId) return;
    this.pointerActive = false;
    this.pointerId = null;
    this.dragging = false;
    this.startCoord = null;
    this.cb.onDragVisual({ from: null, over: null, dx: 0, dy: 0, active: false });
  }

  /** \u4ece from \u671d\u5411 to \u7684\u7b2c\u4e00\u6b65\uff08\u7528\u4e8e\u8df3\u683c\u65f6\u964d\u7ea7\u4e3a\u5355\u6b65\u4ea4\u6362\uff09 */
  private firstStepToward(from: Coord, to: Coord): Coord | null {
    const dc = to.c - from.c;
    const dr = to.r - from.r;
    let step: Coord;
    if (Math.abs(dc) >= Math.abs(dr)) {
      step = { c: from.c + Math.sign(dc), r: from.r };
    } else {
      step = { c: from.c, r: from.r + Math.sign(dr) };
    }
    if (step.c < 0 || step.c >= BOARD_SIZE || step.r < 0 || step.r >= BOARD_SIZE) return null;
    return step;
  }
}
